import { LogUserActivity } from '@/apis/users/requests'
import {
  RefreshFamilyApplication,
  selectApplicationRequestStatus,
  selectFamilyApplication,
  SubmitFamilyApplication,
} from '@/app-storage/applications/familyApplicationSlice.ts'
import { AppDispatch, RootState } from '@/app-storage/store.ts'
import { useAppAlert } from '@/app/AppAlertProvider.tsx'
import { buttonVariants } from '@/app/common/components/Button'
import LoadingCard from '@/app/common/components/LoadingCard'
import { cn } from '@/app/common/utilities'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { getCompletedStudents } from '@/app/pages/family_application/step-checker/student_step_checker'
import { SubmitButton } from '@/app/pages/family_application/student_details/shared/submit-button.tsx'
import { StateQueries } from '@/queries/states/queries'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { setEditMode } from '@/storage/applications/applicationStatusSlice'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { useQuery } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { LOG_TYPES } from '../constants'
import { didNotCompleteIdentityVerification } from '../utils'
import { ReviewItem, ReviewItemProps } from './ReviewItem'
import { ReviewStudentDetails } from './ReviewStudentDetails'
import { getIdentityVerificationUrl } from '@/routing/family_application_route_paths.ts'

type ReviewApplicationPageProps = {
  isPrint?: boolean
}

export default function ReviewApplicationPage({
  isPrint,
}: ReviewApplicationPageProps) {
  const dispatch = useDispatch<AppDispatch>()
  const programId = useSelector(selectProgramID)
  const { t } = useTranslation()
  const { fireAlert } = useAppAlert()
  const familyApplicationState = useSelector(selectFamilyApplication)
  const { request } = useSelector((state: RootState) => state.FamilyApplication)
  const applicationStatus = useSelector(selectApplicationRequestStatus)
  const navigate = useNavigate()
  const programSettings = useSelector(
    (state: RootState) => state.ProgramSettings
  )
  const stateData = useQuery(
    StateQueries.getStateById(familyApplicationState.global_state_id, {
      staleTime: Infinity,
    })
  )

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!isPrint) {
      dispatch(RefreshFamilyApplication(programId))
      if (!hasLoggedPageView.current) {
        LogUserActivity({
          log_type: LOG_TYPES.VIEW_PAGE,
          suffix_text: 'Application review summary.',
        })
        hasLoggedPageView.current = true
      }
    }
  }, [])

  async function handleSubmit() {
    const resultAction = await dispatch(
      SubmitFamilyApplication({
        application_id: familyApplicationState.application_id,
        selected_student_ids: getCompletedStudents(
          familyApplicationState.students,
          familyApplicationState
        ),
      })
    )
    if (SubmitFamilyApplication.fulfilled.match(resultAction)) {
      navigate(AppRoutePaths.family_application.application_sent)
    } else {
      await fireAlert({
        variant: 'error',
        message: t('error_submitting_application'),
      })
    }
  }

  if (!familyApplicationState) {
    return <div className="h-full">{t('no_application_details')}</div>
  }

  function formatPhoneNumber(phoneNumberString: string) {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '')
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3]
    }
    return null
  }

  const formatSchemaValue = (value: string): string => {
    return value
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })

  const fullAddress = () => {
    const values = []
    if (familyApplicationState?.address)
      values.push(familyApplicationState.address)
    if (familyApplicationState?.address2)
      values.push(familyApplicationState.address2)
    if (familyApplicationState?.city) values.push(familyApplicationState?.city)
    if (stateData.data?.name) values.push(stateData.data.name)
    if (familyApplicationState?.zip_code)
      values.push(familyApplicationState.zip_code)

    return values.join(', ')
  }

  const applicantIdentityDetails = [
    {
      title: t('name'),
      value:
        familyApplicationState.first_name +
        ' ' +
        familyApplicationState.last_name,
    },
    {
      title: t('date_of_birth_capital'),
      value: familyApplicationState.date_of_birth
        ? format(parseISO(familyApplicationState.date_of_birth), 'MM-dd-y')
        : '',
    },
    {
      title: t('ssn_last'),
      value: familyApplicationState.SSN,
    },
  ]

  const applicantDetails = [
    {
      title: t('phone_number'),
      value: formatPhoneNumber(String(familyApplicationState.phone)),
    },
    {
      title: t('address'),
      value: fullAddress(),
    },
  ]

  const { non_taxable_income } = familyApplicationState

  const nonTaxableIncome = non_taxable_income
    ? non_taxable_income?.map((item) => formatSchemaValue(item)).join(', ')
    : t('none')

  const householdDetails: ReviewItemProps[] = [
    {
      title: t('household_size'),
      value: familyApplicationState.household_inhabitants ?? 0,
    },
    {
      title: t('your_year_income', {
        year: programSettings.data?.acceptable_income_year,
      }),
      value: familyApplicationState.verified_feed_adjustment_gross_income
        ? currencyFormatter.format(
            familyApplicationState.verified_feed_adjustment_gross_income
          )
        : null,
    },
  ]

  if (familyApplicationState.household_filed_federal_income == 'individually') {
    householdDetails.push({
      title: t('spouse_year_income', {
        year:
          familyApplicationState.spouse_verified_tax_year ??
          programSettings.data?.acceptable_income_year ??
          '',
      }),
      value: currencyFormatter.format(
        familyApplicationState.spouse_verified_gross_income ?? 0
      ),
    })
  }

  const householdDetails2 = [
    {
      title: t('non_taxable_income'),
      value: nonTaxableIncome,
    },
    {
      title: t('attestations'),
      value: (
        <div className="text-gray-500 text-end">
          {t('accepted_on')}{' '}
          {format(
            parseISO(
              familyApplicationState.attestations.slice(-1)[0].accepted_time
            ),
            'MM/dd/yy'
          )}{' '}
          {t('at')}{' '}
          {format(
            parseISO(
              familyApplicationState.attestations.slice(-1)[0].accepted_time
            ),
            'h:mm a'
          )}
        </div>
      ),
    },
  ]

  householdDetails.push(...householdDetails2)

  const hasIdentityFlag = familyApplicationState.flags.find(
    (item) => item.flag_code === 'identity'
  )
  const hasWrongInformation = familyApplicationState.flags.find(
    (item) => item.flag_code === 'clearme_data_inaccurate'
  )

  if (request.status === 'in_flight') {
    return (
      <div>
        <LoadingCard isLoading={true}>{t('loading')}</LoadingCard>
      </div>
    )
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{isPrint ? t('application_summary') : t('application_review')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content
        className={cn('w-full space-y-2', isPrint && 'mt-10')}
      >
        <div className="mb-8">
          <div className="flex items-center justify-between pb-8">
            <h2 className="font-bold text-gray-700">{t('identity')}</h2>
            {!isPrint && (hasIdentityFlag || hasWrongInformation) && (
              <Link
                className={buttonVariants({ variant: 'outline' })}
                to={getIdentityVerificationUrl(programSettings.data!)}
              >
                {t('edit_applicant_details')}
              </Link>
            )}
          </div>
          <div>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700 mb-10">
              {applicantIdentityDetails.map((detail, index) => (
                <ReviewItem
                  key={index}
                  title={detail.title}
                  value={detail.value}
                />
              ))}
            </ul>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between pb-8">
            <h2 className="font-bold text-gray-700">
              {t('applicant_details')}
            </h2>
            {!isPrint &&
              didNotCompleteIdentityVerification(
                familyApplicationState.flags
              ) && (
                <Link
                  to={
                    AppRoutePaths.family_application.residency_verification
                      .index
                  }
                  onClick={() => {
                    dispatch(setEditMode('form-edit-identity-verification'))
                  }}
                  className={buttonVariants({
                    variant: 'outline',
                    size: 'sm',
                  })}
                >
                  {t('edit_residency_details')}
                </Link>
              )}
          </div>
          <ul className="divide-y divide-gray-200 dark:divide-gray-700 mb-10">
            {applicantDetails.map((detail, index) => (
              <ReviewItem
                key={index}
                title={detail.title}
                value={detail.value}
              />
            ))}
          </ul>
        </div>

        <div>
          <div className="flex items-center justify-between pb-4">
            <h2 className="font-bold text-gray-700">{t('student_details')}</h2>
          </div>
          <ReviewStudentDetails
            students={familyApplicationState.students}
            isPrint={isPrint}
          />
        </div>

        {
          <div>
            <div className="flex items-center justify-between pb-4 mt-8">
              <h2 className="font-bold text-gray-700">
                {t('household_income_details')}
              </h2>
              {!isPrint && (
                <Link
                  to={
                    AppRoutePaths.family_application.income_verification.index
                  }
                  onClick={() => {
                    dispatch(setEditMode('form-edit-income-details'))
                  }}
                  className={buttonVariants({
                    variant: 'outline',
                    size: 'sm',
                  })}
                >
                  {t('edit_income_details')}
                </Link>
              )}
            </div>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {householdDetails.map((detail, index) => (
                <ReviewItem
                  key={index}
                  title={detail.title}
                  value={detail.value}
                />
              ))}
            </ul>
          </div>
        }
      </FamilyWizardLayout.Content>
      {!familyApplicationState.submitted_at && (
        <FamilyWizardLayout.ActionBar>
          <SubmitButton
            buttonTxt={t('submit_application')}
            onClick={handleSubmit}
            status={applicationStatus}
          />
        </FamilyWizardLayout.ActionBar>
      )}
    </FamilyWizardLayout>
  )
}
